import { useParams, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Heart, Share2, Shield, Armchair, Disc, Calendar, Truck, Check, ChevronLeft, ChevronRight, ArrowDown, Copy, CheckCircle2, Mail, Printer, Wind, Radio, Sparkles, RotateCw, X, Info, ExternalLink, AlertCircle, MapPin, Sun, Gauge } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../services/api';
import api from '../../services/api';
import { toggleWishlist } from '../../features/wishlist/wishlistSlice';
import { addToWishlist as addToWishlistAPI, removeFromWishlist as removeFromWishlistAPI } from '../../services/wishlistService';
import { addToRecentlyViewed } from '../../services/recentlyViewedService';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import SimilarCars from '../../components/ALLJsx/SimilarCars';
import SocialShare from '../../components/ALLJsx/SocialShare';
import EmailCarDetails from '../../components/ALLJsx/EmailCarDetails';
import VirtualTourViewer from '../../components/ALLJsx/VirtualTourViewer';
import PriceHistoryChart from '../../components/ALLJsx/PriceHistoryChart';
import FinancingCalculator from '../../components/ALLJsx/FinancingCalculator';
import GoodDealBadge from '../../components/ALLJsx/GoodDealBadge';
import { computeMarketComparison } from '../../utils/marketComparison';
import '../AllCss/CarDetails.css';
import { DEFAULT_CARFAX_BADGE_IMAGE_URL, pickPublicCarfaxHref, isCarfaxConsumerPaywallUrl } from '../../constants/carfaxPublic';
import { buildSeoDescription, buildSeoTitle, computeSeoScore, extractSeoKeywords, trackSeoKeywords } from '../../utils/seoTools';
import { buildCarPublicUrl } from '../../utils/carPublicUrl';
import { VEHICLE_PLACEHOLDER_URL } from '../../constants/vehiclePlaceholder';
import { useEnsureCars } from '../../hooks/useEnsureCars';

// 150+ point inspection checklist (used in popup)
const INSPECTION_CATEGORIES = [
  { name: 'Engine', items: ['Engine Performance', 'Emission Controls', 'Engine Filters: oil, air, and emissions', 'Vacuum Hoses & Lines', 'Exhaust System', 'Spark Plugs', 'Secondary Ignition System', 'Intake Manifold', 'Engine Mounts', 'Water Pump', 'Fluid Leaks', 'Timing Belt/Chain', 'Serpentine Belt', 'PCV Valve', 'EGR System', 'Catalytic Converter', 'O2 Sensors', 'Mass Air Flow Sensor', 'Throttle Body', 'Fuel Injectors', 'Compression Test', 'Oil Pressure', 'Engine Noise/Vibration'] },
  { name: 'Cooling System', items: ['Radiator & Cap', 'Radiator/Heater Hoses', 'Cooling Fan', 'Shroud', 'Coolant Level & Condition', 'Hoses (all)', 'Overflow Tank', 'Thermostat', 'Water Pump (operation)', 'Heater Core', 'Cooling System Pressure Test', 'Temperature Gauge Operation'] },
  { name: 'Fuel System', items: ['Fuel Tank', 'Fuel Lines & Hoses', 'Fuel Pump', 'Fuel Filter', 'Fuel Pressure', 'Fuel Cap', 'Evaporative Emissions System', 'Fuel Injector Operation', 'Throttle Position Sensor'] },
  { name: 'Drive Axle', items: ['4-Wheel-Drive Operation', 'All-Wheel-Drive Operation', 'CV Joints', 'CV Boots', 'Universal Joints', 'Axle Shafts', 'Vibration/Backlash', 'Differential Fluid', 'Transfer Case (if applicable)', 'Drive Shaft', 'Axle Bearings'] },
  { name: 'Transmission', items: ['Transmission Fluid Level & Condition', 'Automatic (starts in P and N only)', 'Manual (starts w/ clutch in only)', 'Slipping', 'Shift Operation', 'Noise', 'Transmission Mounts', 'Clutch Operation', 'Fluid Leaks', 'Shift Linkage', 'Transmission Cooler', 'Torque Converter', 'Gear Engagement', 'Reverse Operation'] },
  { name: 'Suspension System', items: ['Frame Integrity: weld marks, wrinkles or creases, undercarriage dents, frame rails, misalignment', 'Ball Joints', 'Springs/Torsion Bars', 'Sway Bar/Links', 'Bushings', 'MacPherson Struts', 'Tires & Wheels: pressure, wear, ratings, brand/axle match, and vibration', 'Front Suspension & Noise', 'Rear Shocks & Suspension', 'Control Arms', 'Stabilizer Links', 'Wheel Bearings', 'Alignment Check', 'Ride Height'] },
  { name: 'Electrical System', items: ['Alternator/Regulator', 'Starter', 'Windshield: wipers and washer', 'Horn', 'Battery: terminals, alternator, and case condition', 'Child Safety Locks', 'Power Locks', 'Gauges: check engine light, warning lights, gas gauge, temperature gauge, speedometer', 'Tachometer, Odometer, Trip Computer', 'Clock', 'Electronic Instrument Cluster', 'Fuses & Relays', 'Wiring Harness', 'Ground Connections', 'Voltage Regulator'] },
  { name: 'Brake System', items: ['Brake Fluid Level & Condition', 'Hoses & Lines', 'Parking Brake', 'Master Cylinder/Booster', 'Pedals (height and pressure)', 'Springs & Linkages', 'Brake Lines', 'Brake Shoes/Pads', 'Brake Rotors', 'Brake Drums', 'Brake Calipers & Cylinders', 'Anti-Lock System', 'Brake Warning Lights', 'Brake Performance Test', 'Parking Brake Operation'] },
  { name: 'Steering System', items: ['Tie Rods', 'Idler Arms', 'Center Links', 'Steering Gear/Rack & Pinion', 'Power Steering Pump', 'Hoses/Lines', 'Shaft & Couplings', 'Alignment', 'Steering Wheel Play', 'Power Steering Fluid', 'Steering Column', 'Steering Response'] },
  { name: 'Body / Interior', items: ['Carpet Condition', 'Seatbelt Operation', 'Upholstery Condition', 'Sun Visors', 'Rearview Mirror', 'Trim & Molding', 'Hood Latches', 'Trunk Release', 'Fuel Door Release', 'Paint Condition', 'Windshield Condition', 'Squeaks, Rattles, or Wind Noise', 'Door Panels', 'Headliner', 'Dashboard', 'Center Console', 'Door Handles', 'Window Seals', 'Body Panels Alignment', 'Rust & Corrosion Check'] },
  { name: 'Heating & A/C System', items: ['Compressor/Clutch', 'Condenser', 'Evaporator', 'Hoses & Lines', 'Blower Fan', 'Temperature/Blower Control: heater, A/C operation, and vent selector', 'Refrigerant Level', 'A/C Performance', 'Heater Performance', 'Defroster Operation', 'Vent Operation', 'Climate Control System'] },
  { name: 'Lighting System', items: ['Brake Lights', 'Tag Lights', 'Turn Signals (front & rear)', 'Interior Lights', 'Dash & Instrument Lights', 'Back-Up Lights', 'Hazard Lights', 'Side Marker Lights', 'Hood Lights', 'Trunk Lights', 'Glove Compartment Lights', 'Headlights (high & low beams)', 'Fog Lights', 'Daytime Running Lights', 'License Plate Lights'] },
  { name: 'Accessories', items: ['Antenna', 'Window Operation (power & manual)', 'Power Seats', 'Audio & Entertainment System: knobs, speakers, presets', 'Cruise Control', 'Warning Chimes', '12V Outlet', 'Sunroof/Convertible Top', 'Navigation System', 'Bluetooth Connectivity', 'USB Ports', 'Keyless Entry', 'Remote Start', 'Backup Camera', 'Parking Sensors'] },
  { name: 'Miscellaneous', items: ['Tilt, Lock & Telescopic Steering Wheel', 'Door & Trunk: seal, lock, and key operation', 'Glove Compartment', 'Exterior Mirrors', 'Wheel Well Moldings', 'Tag Bracket/Mounting', 'Grille/Front Cowl', 'Spare Tire & Jack (if equipped)', 'Remote Entry System', 'Key Fob Operation', 'Tire Pressure Monitoring System', 'Airbag System Check', 'Seat Adjustment Mechanisms', 'Cup Holders', 'Storage Compartments'] }
];

const KEY_FEATURE_SECTIONS = [
  { key: 'comfort', title: 'Comfort and Convenience', icon: Wind, className: 'cdp-feature-comfort' },
  { key: 'technology', title: 'Entertainment and Technology', icon: Radio, className: 'cdp-feature-technology' },
  { key: 'safety', title: 'Safety and Security', icon: Shield, className: 'cdp-feature-safety' },
  { key: 'seating', title: 'Seating and Interior', icon: Armchair, className: 'cdp-feature-interior' },
  { key: 'lighting', title: 'Lighting', icon: Sun, className: 'cdp-feature-lighting' },
  { key: 'performance', title: 'Performance and Drive', icon: Gauge, className: 'cdp-feature-performance' },
  { key: 'roof', title: 'Roof and Glass', icon: Sun, className: 'cdp-feature-roof' },
  { key: 'cargo', title: 'Cargo and Towing', icon: Truck, className: 'cdp-feature-cargo' },
  { key: 'wheels', title: 'Wheels and Tires', icon: Disc, className: 'cdp-feature-wheels' },
  { key: 'braking', title: 'Braking and Traction', icon: Shield, className: 'cdp-feature-braking' },
];

function CarDetails() {
  const { cars: inventoryCars } = useEnsureCars();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const car = useSelector((state) => state.cars.items.find((c) => String(c.stockNumber) === String(id) || String(c.id) === String(id)));
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const wishlistIds = useSelector((state) => state.wishlist.items);
  // Normalize IDs to strings for comparison
  const carIdString = car ? String(car.id) : null;
  const isWishlisted = carIdString ? wishlistIds.some(id => String(id) === carIdString) : false;
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [zipCode, setZipCode] = useState(() => localStorage.getItem('selectedZipcode') || '');
  const [shippingInfo, setShippingInfo] = useState(null);
  const [stockCopied, setStockCopied] = useState(false);
  const [vinCopied, setVinCopied] = useState(false);
  const [paymentType, setPaymentType] = useState('monthly');
  const [calcMonthlyPayment, setCalcMonthlyPayment] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(car?.wishlistCount || 0);
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [showEmailCar, setShowEmailCar] = useState(false);
  const [carImages, setCarImages] = useState(null);
  const [showVirtualTour, setShowVirtualTour] = useState(false);
  const [showInspectionPopup, setShowInspectionPopup] = useState(false);
  /** Hero overlay (stock, price, CTAs) auto-hides after 3s with slide-up */
  const [heroPanelDismissed, setHeroPanelDismissed] = useState(false);
  /** Full GET /api/cars/:id — CARFAX href + badge (partner+VIN resolved on server). */
  const [carfaxFromApi, setCarfaxFromApi] = useState(null);
  const [apiCarDetail, setApiCarDetail] = useState(null);

  const carfaxBadgeImageUrl = DEFAULT_CARFAX_BADGE_IMAGE_URL;

  const carfaxBadgeDims = useMemo(() => {
    const pick = (v) => {
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
      if (typeof v === 'string' && v.trim()) {
        const n = parseInt(v, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    };
    const w =
      pick(car?.carfaxBadgeWidth) ??
      pick(car?.carfax_badge_width) ??
      155;
    const h =
      pick(car?.carfaxBadgeHeight) ??
      pick(car?.carfax_badge_height) ??
      56;
    return { width: w, height: h };
  }, [car]);

  const carfaxReportHref = useMemo(() => {
    const fetched = carfaxFromApi?.reportHref;
    if (typeof fetched === 'string' && fetched.trim() && !isCarfaxConsumerPaywallUrl(fetched)) {
      const t = fetched.trim();
      if (/^https?:\/\//i.test(t)) return t;
    }
    return pickPublicCarfaxHref(car);
  }, [car, carfaxFromApi]);

  /** Dealer-entered facts from reviewing the history report (not auto-fetched from CARFAX). */
  const historyHighlights = useMemo(() => {
    const rawOwner = car?.historyOwnerCount ?? car?.history_owner_count;
    const n = rawOwner != null ? parseInt(String(rawOwner), 10) : NaN;
    const ownerCount = Number.isFinite(n) && n >= 1 && n <= 99 ? n : null;
    const flood = car?.historyNoFloodFrameDamage ?? car?.history_no_flood_frame_damage;
    const odo = car?.historyNoOdometerIssues ?? car?.history_no_odometer_issues;
    const floodOk = flood === true || flood === false ? flood : null;
    const odoOk = odo === true || odo === false ? odo : null;
    return { ownerCount, flood: floodOk, odometer: odoOk };
  }, [car]);

  const hasHistoryHighlights =
    historyHighlights.ownerCount != null ||
    historyHighlights.flood != null ||
    historyHighlights.odometer != null;

  const handlePrint = () => {
    window.print();
  };

  const overviewRef = useRef(null);
  const vehicleDetailsRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const inspectionRef = useRef(null);
  const contentStartRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    const orig = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    setTimeout(() => { html.style.scrollBehavior = orig; }, 0);
  }, [id]);

  // Hero overlay (stock, price, Schedule Test Drive, View details): show ~3s then slide up
  useEffect(() => {
    setHeroPanelDismissed(false);
    const t = setTimeout(() => setHeroPanelDismissed(true), 3000);
    return () => clearTimeout(t);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'overview', ref: overviewRef },
        { id: 'vehicle-details', ref: vehicleDetailsRef },
        { id: 'features', ref: featuresRef },
        { id: 'pricing', ref: pricingRef },
        { id: 'inspection', ref: inspectionRef },
      ];
      const pos = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].ref.current && pos >= sections[i].ref.current.offsetTop) {
          setActiveTab(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (ref) => {
    if (ref.current) window.scrollTo({ top: ref.current.offsetTop - 150, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchShipping = async () => {
      if (!zipCode || zipCode.length !== 5) { setShippingInfo(null); return; }
      try {
        const res = await axios.get(`${API_URL}/api/shipping/calculate?zipcode=${zipCode}`);
        if (res.data.success) setShippingInfo(res.data.data);
      } catch { setShippingInfo(null); }
    };
    fetchShipping();
    const handler = (e) => setZipCode(e.detail.zipcode);
    window.addEventListener('zipcodeChanged', handler);
    return () => window.removeEventListener('zipcodeChanged', handler);
  }, [zipCode]);

  useEffect(() => {
    if (id) axios.post(`${API_URL}/api/cars/${id}/view`).catch(() => { });
  }, [id]);

  // Fetch fresh car data with images from API
  useEffect(() => {
    setCarfaxFromApi(null);
    setApiCarDetail(null);
  }, [id]);

  useEffect(() => {
    const fetchCarData = async () => {
      if (!id) return;

      try {
        const response = await axios.get(`${API_URL}/api/cars/${id}`);
        if (response.data.success && response.data.data?.car) {
          const carData = response.data.data.car;
          setApiCarDetail(carData);
          setCarfaxFromApi({
            reportHref: pickPublicCarfaxHref(carData),
          });

          // Check if car is sold or deleted
          if (carData.status === 'sold' || carData.status === 'delivered') {
            console.warn('Car is sold/delivered, redirecting to not found');
            navigate('/not-found', { replace: true, state: { from: location.pathname, reason: 'sold' } });
            return;
          }

          if (carData.images && Array.isArray(carData.images) && carData.images.length > 0) {
            setCarImages(carData.images);
          }
        }
      } catch (error) {
        console.error('Error fetching car data:', error);
        // Handle 404 or car not found errors
        if (error.response?.status === 404 || error.response?.status === 400) {
          console.warn('Car not found or deleted, redirecting to not found');
          navigate('/not-found', { replace: true, state: { from: location.pathname, reason: 'not_found' } });
        }
      }
    };

    fetchCarData();
  }, [id, navigate, location.pathname]);

  // Track recently viewed car - save to both API (if authenticated) and localStorage
  useEffect(() => {
    if (!car || !car.id) return;

    const trackRecentlyViewed = async () => {
      // Always save to localStorage as fallback
      addToRecentlyViewed(car);

      // Also save to API database if user is authenticated
      if (isAuthenticated) {
        try {
          await api.post('/recently-viewed', { carId: car.id });
        } catch (error) {
          console.error('Error adding car to recently viewed API:', error);
          // Continue even if API fails, localStorage is saved
        }
      }
    };

    trackRecentlyViewed();
  }, [car, isAuthenticated]);

  // Fetch and update wishlist count from API (for all users to see total count)
  useEffect(() => {
    const fetchWishlistCount = async () => {
      if (!id || !car) return;

      try {
        const response = await axios.get(`${API_URL}/api/cars/${car.id || car.stockNumber || id}`);
        if (response.data.success && response.data.data?.car) {
          const carData = response.data.data.car;
          if (carData.wishlistCount !== undefined && carData.wishlistCount !== null) {
            setWishlistCount(carData.wishlistCount);
          }
        }
      } catch (error) {
        console.error('Error fetching wishlist count:', error);
      }
    };

    // Fetch immediately
    fetchWishlistCount();

    // Update count every 5 seconds to catch updates from other users
    const interval = setInterval(fetchWishlistCount, 5000);

    return () => clearInterval(interval);
  }, [id, car?.id, car?.stockNumber, car]);

  // Update wishlist count when car data changes (fallback)
  useEffect(() => {
    if (car?.wishlistCount !== undefined && car.wishlistCount !== null) {
      setWishlistCount(car.wishlistCount);
    }
  }, [car?.wishlistCount]);

  // Process images - handle various formats (array, string, JSON string, etc.)
  const processImages = () => {
    if (!car) {
      return [VEHICLE_PLACEHOLDER_URL];
    }

    // Use carImages state if available (fetched from API), otherwise use car.images
    let imageArray = carImages || car.images;

    // If images is null, undefined, or empty array
    if (!imageArray || (Array.isArray(imageArray) && imageArray.length === 0)) {
      return [VEHICLE_PLACEHOLDER_URL];
    }

    // If it's a string, try to parse it as JSON
    if (typeof imageArray === 'string') {
      try {
        imageArray = JSON.parse(imageArray);
      } catch (e) {
        // If not JSON, treat as single image URL
        imageArray = [imageArray];
      }
    }

    // Ensure it's an array
    if (!Array.isArray(imageArray)) {
      imageArray = [imageArray];
    }

    // Filter out empty, null, or undefined values
    imageArray = imageArray.filter(img => {
      if (!img) return false;
      if (typeof img === 'string' && (img.trim() === '' || img === 'null' || img === 'undefined')) return false;
      return true;
    });

    // Process image URLs - if they start with /api/, prefix with API_URL
    imageArray = imageArray.map(img => {
      if (typeof img === 'string' && img.startsWith('/api/')) {
        const fullUrl = `${API_URL}${img}`;
        return fullUrl;
      }
      // If it's already a full URL (http:// or https://), use as is
      if (typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://'))) {
        return img;
      }
      return img;
    });


    // If no valid images, return placeholder
    if (imageArray.length === 0) {
      return [VEHICLE_PLACEHOLDER_URL];
    }

    return imageArray;
  };

  const images = processImages();

  // Get virtual tour images (if available)
  const getVirtualTourImages = () => {
    if (!car?.virtualTourImages) return [];

    try {
      if (Array.isArray(car.virtualTourImages)) {
        return car.virtualTourImages;
      }
      if (typeof car.virtualTourImages === 'string') {
        const parsed = JSON.parse(car.virtualTourImages);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error('Error parsing virtual tour images:', e);
    }
    return [];
  };

  // Get virtual tour views (multiple views: exterior, interior, etc.)
  const getVirtualTourViews = () => {
    if (!car?.virtualTourViews) return null;

    try {
      if (typeof car.virtualTourViews === 'object' && !Array.isArray(car.virtualTourViews)) {
        return car.virtualTourViews;
      }
      if (typeof car.virtualTourViews === 'string') {
        const parsed = JSON.parse(car.virtualTourViews);
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error parsing virtual tour views:', e);
    }
    return null;
  };

  const virtualTourImages = getVirtualTourImages();
  const virtualTourViews = getVirtualTourViews();
  const hasVirtualTour = virtualTourImages.length > 0 || (virtualTourViews && Object.keys(virtualTourViews).length > 0);

  const formatPrice = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(p);
  const formatMiles = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const miles = Number(value);
    return `${miles.toLocaleString()} miles`;
  };
  const marketInsight = useMemo(
    () => (car ? computeMarketComparison(car, inventoryCars) : null),
    [car, inventoryCars]
  );

  const fallbackMonthly = car
    ? Math.round((parseFloat(car.price) * (0.0699 / 12) * Math.pow(1 + 0.0699 / 12, 72)) / (Math.pow(1 + 0.0699 / 12, 72) - 1))
    : 0;
  const monthlyPayment = calcMonthlyPayment ?? fallbackMonthly;
  const seoKeywords = useMemo(() => extractSeoKeywords(car), [car]);
  const seoTitle = useMemo(() => buildSeoTitle(car), [car]);
  const seoDescription = useMemo(
    () => buildSeoDescription(car, car?.description),
    [car]
  );
  const seoScore = useMemo(
    () =>
      computeSeoScore({
        title: seoTitle,
        description: seoDescription,
        keywords: seoKeywords,
        hasImages: Array.isArray(images) && images.length > 0,
        hasVin: Boolean(car?.vin),
        hasPrice: Boolean(car?.price),
      }),
    [seoTitle, seoDescription, seoKeywords, images, car]
  );

  useEffect(() => {
    if (!car) return;
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    const previousKeywords = document.querySelector('meta[name="keywords"]')?.getAttribute('content') || '';

    const upsertMeta = (selector, attrs) => {
      let meta = document.head.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => meta.setAttribute(k, v));
        document.head.appendChild(meta);
      }
      return meta;
    };

    document.title = seoTitle;
    upsertMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', seoDescription);
    upsertMeta('meta[name="keywords"]', { name: 'keywords' }).setAttribute('content', seoKeywords.join(', '));
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', seoTitle);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', seoDescription);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }).setAttribute('content', 'product');

    const pageUrl = buildCarPublicUrl(car);
    let ogImage = images[0];
    if (typeof ogImage === 'string' && ogImage.startsWith('/api/')) {
      ogImage = `${API_URL}${ogImage}`;
    }
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', pageUrl);
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image' }).setAttribute('content', ogImage);
    }
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }).setAttribute('content', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute('content', seoTitle);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute('content', seoDescription);
    if (ogImage) {
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }).setAttribute('content', ogImage);
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    let jsonLd = document.getElementById('car-jsonld');
    const previousJsonLd = jsonLd?.textContent || '';
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'car-jsonld';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Car',
      name: `${car.year} ${car.make} ${car.model}`.trim(),
      description: seoDescription,
      url: pageUrl,
      ...(ogImage ? { image: ogImage } : {}),
      ...(car.vin ? { vehicleIdentificationNumber: car.vin } : {}),
      ...(car.price
        ? {
            offers: {
              '@type': 'Offer',
              price: String(car.price),
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              url: pageUrl,
            },
          }
        : {}),
      ...(car.mileage != null
        ? {
            mileageFromOdometer: {
              '@type': 'QuantitativeValue',
              value: Number(car.mileage),
              unitCode: 'SMI',
            },
          }
        : {}),
    };
    jsonLd.textContent = JSON.stringify(structuredData);

    trackSeoKeywords({
      stockNumber: car.stockNumber || car.id,
      keywords: seoKeywords,
    });

    return () => {
      document.title = previousTitle;
      upsertMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', previousDescription);
      upsertMeta('meta[name="keywords"]', { name: 'keywords' }).setAttribute('content', previousKeywords);
      if (canonical && previousCanonical) {
        canonical.setAttribute('href', previousCanonical);
      }
      const ld = document.getElementById('car-jsonld');
      if (ld) {
        if (previousJsonLd) ld.textContent = previousJsonLd;
        else ld.remove();
      }
    };
  }, [car, seoTitle, seoDescription, seoKeywords, images]);

  if (!car) {
    return <Navigate to="/not-found" replace state={{ from: location.pathname }} />;
  }

  const displayCar = apiCarDetail || car;
  const isPartnerListing =
    displayCar?.listingSource === 'partner' || displayCar?.listing_source === 'partner';
  const partnerDisplayName =
    displayCar?.partnerDealer?.displayName ||
    displayCar?.partner_dealer?.displayName ||
    'Partner Dealer';

  const contactPartnerListingUrl = `/contact?stock=${encodeURIComponent(car.stockNumber || id)}&partner=1`;

  const handleCopyStockNumber = async () => {
    if (car.stockNumber) {
      try {
        await navigator.clipboard.writeText(car.stockNumber);
        setStockCopied(true);
        setTimeout(() => setStockCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = car.stockNumber;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setStockCopied(true);
        setTimeout(() => setStockCopied(false), 2000);
      }
    }
  };

  const handleCopyVIN = async () => {
    if (car.vin) {
      try {
        await navigator.clipboard.writeText(car.vin);
        setVinCopied(true);
        setTimeout(() => setVinCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = car.vin;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setVinCopied(true);
        setTimeout(() => setVinCopied(false), 2000);
      }
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      // Store the car ID they want to like in localStorage
      if (car) {
        localStorage.setItem('pendingWishlistCarId', String(car.id));
      }
      // Redirect to auth page with current location as redirect (includes pathname and search params)
      const redirectUrl = location.pathname + location.search;
      navigate(`/auth?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    if (!car) return;

    try {
      if (isWishlisted) {
        // Remove from wishlist
        await removeFromWishlistAPI(car.id);
        dispatch(toggleWishlist(car.id));
        // Update count optimistically
        setWishlistCount(prev => Math.max(0, prev - 1));
      } else {
        // Add to wishlist
        await addToWishlistAPI(car.id);
        dispatch(toggleWishlist(car.id));
        // Update count optimistically
        setWishlistCount(prev => prev + 1);
      }

      // Refresh car data to get accurate count
      try {
        const response = await axios.get(`${API_URL}/api/cars/${car.id || car.stockNumber}`);
        if (response.data.success && response.data.data?.car) {
          const updatedCar = response.data.data.car;
          if (updatedCar.wishlistCount !== undefined) {
            setWishlistCount(updatedCar.wishlistCount);
          }
        }
      } catch (err) {
        console.error('Error fetching updated car data:', err);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      // Still update Redux for optimistic UI, but show error
      dispatch(toggleWishlist(car.id));
    }
  };

  const getDeliveryDate = (days) => {
    if (!days) return null;
    const d = new Date();
    d.setDate(d.getDate() + (typeof days === 'string' ? parseInt(days.split('-')[1] || days) : days));
    return `Delivered by ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]}`;
  };

  return (
    <div className="cdp cdp-immersive">
      <Navbar />

      {/* Breadcrumb stays fixed below navbar so it stays visible; not trapped under 100vh hero */}
      <div className="cdp-breadcrumb-bar">
        <nav className="cdp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/inventory">All Cars</Link>
          <span>/</span>
          <Link to={`/inventory?make=${encodeURIComponent(car.make)}`}>{car.make}</Link>
          <span>/</span>
          <span className="current">{car.model}</span>
        </nav>
      </div>

      {/* Immersive full-viewport hero — collapses after overlay dismisses so gallery moves up */}
      <section
        className={`cdp-hero-immersive${heroPanelDismissed ? ' cdp-hero-immersive--compact' : ''}`}
        aria-label="Vehicle highlight"
      >
        <div className="cdp-hero-bg" style={{ backgroundImage: `url(${images[0]})` }} aria-hidden="true" />
        <div className="cdp-hero-overlay" aria-hidden="true" />
        <div
          className={`cdp-hero-panel${heroPanelDismissed ? ' cdp-hero-panel--dismissed' : ''}`}
          aria-hidden={heroPanelDismissed}
        >
          <div className="cdp-hero-content">
            <p className="cdp-hero-meta">{car.condition || 'Pre-owned'} · Stock #{car.stockNumber}</p>
            {isPartnerListing && (
              <p className="cdp-partner-badge" role="status">
                Partner dealer — {partnerDisplayName}
              </p>
            )}
            <h1 className="cdp-hero-title">
              {car.year} {car.make} {car.model}{car.trim ? ` ${car.trim}` : ''}
            </h1>
            <div className="cdp-hero-details">
              <GoodDealBadge insight={marketInsight} variant="detail" />
              <span className="cdp-hero-price">{formatPrice(car.price)}</span>
              {car.mileage && (
                <>
                  <span className="cdp-hero-sep">·</span>
                  <span className="cdp-hero-miles">{formatMiles(car.mileage)}</span>
                </>
              )}
            </div>
            <div className="cdp-hero-actions">
              {isPartnerListing ? (
                <button
                  className="cdp-hero-cta"
                  onClick={() => navigate(contactPartnerListingUrl)}
                >
                  <Mail size={20} /> Contact MyCarSlot
                </button>
              ) : (
                <button
                  className="cdp-hero-cta"
                  onClick={() => {
                    if (isAuthenticated && user) navigate(`/appointment/${car.stockNumber}`);
                    else navigate(`/auth?redirect=/appointment/${car.stockNumber}`);
                  }}
                >
                  <Calendar size={20} /> Schedule Test Drive
                </button>
              )}
              <button
                className={isWishlisted ? 'cdp-hero-action active' : 'cdp-hero-action'}
                onClick={handleWishlistToggle}
                title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <Heart size={20} fill={isWishlisted ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="cdp-hero-scroll-hint"
            onClick={() => contentStartRef.current?.scrollIntoView({ behavior: 'smooth' })}
            aria-label="Scroll to details"
          >
            <span>View details</span>
            <div className="cdp-hero-scroll-line" aria-hidden="true" />
          </button>
        </div>
      </section>

      <div ref={contentStartRef} className="cdp-container">
        <div className="cdp-layout">
          <main className="cdp-main">
            <div className="cdp-gallery">
              <div className="cdp-gallery-main">
                <img
                  src={images[selectedImage]}
                  alt={`${car.year} ${car.make} ${car.model}`}
                  onError={(e) => {
                    console.error('Image failed to load:', images[selectedImage]);
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = VEHICLE_PLACEHOLDER_URL;
                  }}
                  onLoad={() => {
                  }}
                />
                {images.length > 1 && (
                  <>
                    <button className="cdp-gallery-nav prev" onClick={() => setSelectedImage(p => p === 0 ? images.length - 1 : p - 1)}><ChevronLeft /></button>
                    <button className="cdp-gallery-nav next" onClick={() => setSelectedImage(p => p === images.length - 1 ? 0 : p + 1)}><ChevronRight /></button>
                  </>
                )}
                {hasVirtualTour && (
                  <button
                    className="cdp-virtual-tour-btn"
                    onClick={() => setShowVirtualTour(true)}
                    title="View 360° Virtual Tour"
                  >
                    <RotateCw size={20} />
                    <span>360° View</span>
                  </button>
                )}
              </div>
              {images.length > 1 && (
                <div className="cdp-thumbs">
                  {images.map((img, i) => (
                    <button key={i} className={`cdp-thumb ${i === selectedImage ? 'active' : ''}`} onClick={() => setSelectedImage(i)}>
                      <img
                        src={img}
                        alt={`${car.year} ${car.make} ${car.model} - ${i + 1}`}
                        onError={(e) => {
                          console.error('Thumbnail failed to load:', img);
                          e.target.onerror = null; // Prevent infinite loop
                          e.target.src = VEHICLE_PLACEHOLDER_URL;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <nav className="cdp-tabs">
              {[
                { id: 'overview', label: 'Overview', ref: overviewRef },
                { id: 'vehicle-details', label: 'Vehicle Details', ref: vehicleDetailsRef },
                { id: 'features', label: 'Features', ref: featuresRef },
                { id: 'pricing', label: 'Pricing', ref: pricingRef },
                { id: 'inspection', label: 'History & Inspection', ref: inspectionRef },
              ].map(t => (
                <button key={t.id} className={activeTab === t.id ? 'active' : ''} onClick={() => scrollToSection(t.ref)}>{t.label}</button>
              ))}
            </nav>

            <div className="cdp-content">
              <section ref={overviewRef} className="cdp-section" id="overview">
                <h2>Why You'll Love This Car</h2>
                <p>{seoDescription || (car.description?.trim() || `Explore this ${car.year} ${car.make} ${car.model}—contact us for details, history, and availability.`)}</p>
                <p className="cdp-nodata">SEO score: {seoScore}/100 · Keywords: {seoKeywords.slice(0, 6).join(', ') || 'N/A'}</p>
              </section>

              <section ref={vehicleDetailsRef} className="cdp-section" id="vehicle-details">
                <h2>Vehicle Specifications</h2>
                <div className="cdp-specs">
                  {[
                    ['VIN', car.vin],
                    ['Stock #', car.stockNumber],
                    ['Year', car.year],
                    ['Make', car.make],
                    ['Model', car.model],
                    ['Condition', car.condition || 'Used'],
                    ['Body Type', car.bodyType],
                    ['Engine', car.engineType],
                    ['Transmission', car.transmission],
                    ['Drivetrain', car.drivetrain],
                    ['Fuel Type', car.fuelType],
                    ['MPG City', car.mpgCity],
                    ['MPG Highway', car.mpgHighway],
                    ['Exterior', car.exteriorColor],
                    ['Interior', car.interiorColor],
                    ['Seats', car.seats]
                  ].map(([k, v]) => (
                    <div key={k} className="cdp-spec">
                      <span>{v || 'N/A'}</span>
                      <span>{k}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section ref={featuresRef} className="cdp-section cdp-section-gray" id="features">
                <div className="cdp-features-header">
                  <h2>Key Features</h2>
                  <p className="cdp-features-subtitle">Discover what makes this vehicle special</p>
                </div>
                {car.features ? (
                  <div className="cdp-features">
                    {KEY_FEATURE_SECTIONS.map(({ key, title, icon: Icon, className }) => {
                      const items = car.features[key];
                      if (!items?.length) return null;
                      return (
                        <div key={key} className={`cdp-feature-card ${className}`}>
                          <div className="cdp-feature-icon-wrapper">
                            <div className="cdp-feature-icon">
                              <Icon size={24} />
                            </div>
                          </div>
                          <div className="cdp-feature-body">
                            <h3>{title}</h3>
                            <ul>{items.map((f, i) => (
                              <li key={i}>
                                <span className="cdp-feature-check"><Check size={16} /></span>
                                <span>{f}</span>
                              </li>
                            ))}</ul>
                          </div>
                        </div>
                      );
                    })}
                    {car.interiorColor && !car.features?.seating?.length && (
                      <div className="cdp-feature-card cdp-feature-interior">
                        <div className="cdp-feature-icon-wrapper">
                          <div className="cdp-feature-icon">
                            <Armchair size={24} />
                          </div>
                        </div>
                        <div className="cdp-feature-body">
                          <h3>Seating and Interior</h3>
                          <ul>
                            <li>
                              <span className="cdp-feature-check"><Check size={16} /></span>
                              <span>{car.interiorColor} Interior</span>
                            </li>
                            {car.seats && (
                              <li>
                                <span className="cdp-feature-check"><Check size={16} /></span>
                                <span>{car.seats} Seats</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="cdp-features-empty">
                    <Sparkles size={48} className="cdp-features-empty-icon" />
                    <p className="cdp-nodata">Feature details coming soon.</p>
                  </div>
                )}
              </section>

              <section ref={pricingRef} className="cdp-section" id="pricing">
                <h2>Payment Options</h2>

                {/* Payment Type Toggle */}
                <div className="cdp-payment-toggle">
                  <button
                    className={`cdp-toggle-btn ${paymentType === 'monthly' ? 'active' : ''}`}
                    onClick={() => setPaymentType('monthly')}
                  >
                    Pay monthly
                  </button>
                  <button
                    className={`cdp-toggle-btn ${paymentType === 'cash' ? 'active' : ''}`}
                    onClick={() => setPaymentType('cash')}
                  >
                    Pay with cash
                  </button>
                </div>

                {/* Qualification Badge */}
                {paymentType === 'monthly' && (
                  <div className="cdp-prequalified">
                    <div className="cdp-prequalified-badge">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" fill="#10b981" stroke="#fff" strokeWidth="2" />
                        <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Pre-qualified
                    </div>
                  </div>
                )}

                {/* Main Pricing Grid */}
                <div className="cdp-pricing-grid">
                  {/* Left Column - Payment Details */}
                  <div className="cdp-pricing-col">
                    {paymentType === 'monthly' ? (
                      <>
                        <div className="cdp-pricing-big">
                          <div className="cdp-pricing-value">$0</div>
                          <div className="cdp-pricing-desc">Total due at order</div>
                        </div>

                        <div className="cdp-pricing-monthly">
                          <div className="cdp-monthly-value">${monthlyPayment}<span>/mo</span></div>
                          <div className="cdp-monthly-desc">Monthly payment</div>
                          <div className="cdp-monthly-terms">Based on a 19.49% APR for 72 months.</div>
                        </div>

                        <div className="cdp-pricing-section-title">Your payment terms include</div>
                        <div className="cdp-payment-perk">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="7" fill="#10b981" />
                            <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>$10/mo discount with auto pay</span>
                        </div>

                        <div className="cdp-pricing-section-title">$0 due at order</div>
                        <div className="cdp-pricing-items">
                          <div className="cdp-pricing-item">
                            <span className="cdp-item-arrow">›</span>
                            <span>$0 cash down</span>
                          </div>
                          <div className="cdp-pricing-item">
                            <span className="cdp-item-arrow">›</span>
                            <span>Shipping estimate shown; final quote at checkout</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="cdp-pricing-big">
                          <div className="cdp-pricing-value">{formatPrice(car.price)}</div>
                          <div className="cdp-pricing-desc">Total purchase price</div>
                        </div>

                        <div className="cdp-pricing-section-title">What's included</div>
                        <div className="cdp-pricing-items">
                          <div className="cdp-pricing-item">
                            <span className="cdp-item-arrow">›</span>
                            <span>Vehicle price: {formatPrice(car.price)}</span>
                          </div>
                          <div className="cdp-pricing-item">
                            <span className="cdp-item-arrow">›</span>
                            <span>Tax, title, and registration (estimated)</span>
                          </div>
                          <div className="cdp-pricing-item">
                            <span className="cdp-item-arrow">›</span>
                            <span>Shipping quote finalized at checkout</span>
                          </div>
                        </div>

                        <div className="cdp-pricing-note">
                          Save time and money by paying in full. Final amount will be calculated during checkout.
                        </div>
                      </>
                    )}
                  </div>

                  {/* Right Column - Purchase Price */}
                  <div className="cdp-pricing-col">
                    <div className="cdp-pricing-section-title">Your total purchase price</div>
                    <div className="cdp-pricing-list">
                      <div className="cdp-pricing-row">
                        <span>Vehicle price</span>
                        <span>{formatPrice(car.price)}</span>
                      </div>
                      <div className="cdp-pricing-row">
                        <span>Shipping fee</span>
                        <span>{shippingInfo ? `$${shippingInfo.shippingCost}` : 'Calculated at checkout'}</span>
                      </div>
                      <div className="cdp-pricing-row">
                        <span>Estimated tax, title and registration</span>
                        <span>Example only</span>
                      </div>
                      <div className="cdp-pricing-row total">
                        <span>Total purchase price (estimate)</span>
                        <span>{formatPrice(car.price)}+ fees</span>
                      </div>
                    </div>

                    <div className="cdp-pricing-note">
                      Tax, title, and registration fees vary by state and are estimates. Final amount will be calculated during checkout.
                    </div>
                  </div>
                </div>

                {paymentType === 'monthly' && (
                  <FinancingCalculator
                    vehiclePrice={car.price}
                    onMonthlyChange={setCalcMonthlyPayment}
                  />
                )}

                {marketInsight && (
                  <p className="cdp-market-insight" role="note">
                    {marketInsight.isGoodDeal
                      ? `Priced about ${marketInsight.pctBelow}% below the median of ${marketInsight.peerCount} similar ${car.make} ${car.model} listings in our inventory.`
                      : marketInsight.isAboveMarket
                        ? `Priced about ${Math.abs(marketInsight.pctBelow)}% above the median of ${marketInsight.peerCount} similar listings.`
                        : `Comparable to ${marketInsight.peerCount} similar ${car.make} ${car.model} listings (median ${formatPrice(marketInsight.marketMedian)}).`}
                  </p>
                )}

                {car?.id && <PriceHistoryChart carId={car.id} />}
              </section>

              <section ref={inspectionRef} className="cdp-section cdp-verify-section" id="inspection">
                <div className="cdp-verify-header">
                  <span className="cdp-verify-badge">Full transparency</span>
                  <h2 className="cdp-verify-title">History & Inspection</h2>
                  <p className="cdp-verify-subtitle">We believe you deserve to know exactly what you're getting. No surprises, no hidden issues.</p>
                </div>

                <div className="cdp-verify-cards">
                  <div className="cdp-verify-card cdp-verify-card-inspection">
                    <div className="cdp-verify-card-badge">
                      <span className="cdp-verify-number">150+</span>
                      <span className="cdp-verify-label">points</span>
                    </div>
                    <div className="cdp-verify-card-body">
                      <h3>Multi-point inspection</h3>
                      <p>Each vehicle passes our comprehensive checklist before it's approved for sale. Our technicians verify everything from engine performance to interior trim.</p>
                      <ul>
                        <li><Check size={16} /><span>Tires & wheels — 15 checks</span></li>
                        <li><Check size={16} /><span>Cosmetic photos available</span></li>
                        <li><Check size={16} /><span>Interior reconditioned</span></li>
                      </ul>
                      <button className="cdp-verify-cta" onClick={() => setShowInspectionPopup(true)}>
                        See full inspection checklist
                      </button>
                    </div>
                  </div>

                  <div className="cdp-verify-card cdp-verify-card-history">
                    <div className="cdp-verify-card-body">
                      <h3>Vehicle history</h3>
                      <p>
                        {carfaxReportHref
                          ? 'Open the official CARFAX report in a new tab when the dealer has linked one for this vehicle.'
                          : 'When available, the dealer can attach a CARFAX report link so you can review title history, reported incidents, and odometer readings.'}
                      </p>
                      <ul>
                        <li>
                          <Check size={16} />
                          <span>{carfaxReportHref ? 'CARFAX report linked for this VIN' : 'CARFAX link added when provided by the dealer'}</span>
                        </li>
                        <li>
                          <Check size={16} />
                          <span>Always confirm open recalls at NHTSA.gov</span>
                        </li>
                      </ul>
                      <div className="cdp-history-actions">
                        {!carfaxReportHref ? (
                          <span className="cdp-history-muted">No CARFAX link on file yet.</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {hasHistoryHighlights ? (
                  <div
                    className="cdp-history-highlights"
                    role="region"
                    aria-label="Vehicle history highlights"
                  >
                    {historyHighlights.ownerCount != null ? (
                      <div className="cdp-history-highlight-row">
                        <Check className="cdp-history-highlight-icon cdp-history-highlight-icon--ok" size={20} aria-hidden />
                        <span className="cdp-history-highlight-text">
                          {historyHighlights.ownerCount}{' '}
                          {historyHighlights.ownerCount === 1 ? 'Owner' : 'Owners'}
                        </span>
                        <Info
                          className="cdp-history-highlight-info"
                          size={18}
                          aria-hidden
                          title="Based on the vehicle history report reviewed when this vehicle was listed."
                        />
                      </div>
                    ) : null}
                    {historyHighlights.flood != null ? (
                      <div className="cdp-history-highlight-row">
                        {historyHighlights.flood ? (
                          <Check className="cdp-history-highlight-icon cdp-history-highlight-icon--ok" size={20} aria-hidden />
                        ) : (
                          <AlertCircle className="cdp-history-highlight-icon cdp-history-highlight-icon--warn" size={20} aria-hidden />
                        )}
                        <span className="cdp-history-highlight-text">
                          {historyHighlights.flood
                            ? 'No flood or frame damage'
                            : 'Flood or frame damage reported'}
                        </span>
                        <Info
                          className="cdp-history-highlight-info"
                          size={18}
                          aria-hidden
                          title="Based on the vehicle history report reviewed when this vehicle was listed. Open the full report for details."
                        />
                      </div>
                    ) : null}
                    {historyHighlights.odometer != null ? (
                      <div className="cdp-history-highlight-row">
                        {historyHighlights.odometer ? (
                          <Check className="cdp-history-highlight-icon cdp-history-highlight-icon--ok" size={20} aria-hidden />
                        ) : (
                          <AlertCircle className="cdp-history-highlight-icon cdp-history-highlight-icon--warn" size={20} aria-hidden />
                        )}
                        <span className="cdp-history-highlight-text">
                          {historyHighlights.odometer
                            ? 'No odometer problems'
                            : 'Odometer issue reported'}
                        </span>
                        <Info
                          className="cdp-history-highlight-info"
                          size={18}
                          aria-hidden
                          title="Based on the vehicle history report reviewed when this vehicle was listed. Open the full report for details."
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {carfaxReportHref ? (
                  <div className="cdp-carfax-bar">
                    <img
                      src={carfaxBadgeImageUrl}
                      alt="Show me the CARFAX"
                      width={carfaxBadgeDims.width}
                      height={carfaxBadgeDims.height}
                      className="cdp-carfax-left-logo"
                      loading="lazy"
                      decoding="async"
                    />
                    <a
                      href={carfaxReportHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cdp-carfax-view-link"
                      aria-label="View CARFAX Vehicle History Report (opens in a new tab)"
                    >
                      <span className="cdp-carfax-view-text">View Report</span>
                      <ExternalLink size={16} aria-hidden />
                    </a>
                  </div>
                ) : null}

                <div className="cdp-verify-recall">
                  <Info size={18} />
                  <p>Some vehicles may have unrepaired safety recalls. <a href="https://www.nhtsa.gov/recalls" target="_blank" rel="noopener noreferrer">Check recalls at NHTSA.gov</a></p>
                </div>
              </section>
            </div>

            {/* Similar Cars Section */}
            <SimilarCars currentCar={car} />
          </main>

          <aside className="cdp-sidebar">
            <div className="cdp-sidebar-inner">
              <div className="cdp-sidebar-header">
                <div className="cdp-sidebar-top-row">
                  <span className="cdp-badge">{car.condition?.toUpperCase() || 'USED'}</span>
                  <div className="cdp-actions">
                    <button onClick={() => setShowSocialShare(true)} title="Share this car">
                      <Share2 size={18} />
                    </button>
                    <button onClick={() => setShowEmailCar(true)} title="Email car details">
                      <Mail size={18} />
                    </button>
                    <button onClick={handlePrint} title="Print car details">
                      <Printer size={18} />
                    </button>
                    <button className={isWishlisted ? 'active' : ''} onClick={handleWishlistToggle} title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}>
                      <Heart size={18} fill={isWishlisted ? 'currentColor' : 'none'} />
                      {wishlistCount > 0 && <span className="cdp-wishlist-count">{wishlistCount}</span>}
                    </button>
                  </div>
                </div>
                <h1
                  className="cdp-sidebar-heading"
                  title={`${car.year} ${car.make} ${car.model}${car.trim ? ` ${car.trim}` : ''}`}
                >
                  <span className="cdp-sidebar-title-row">
                    {car.year} {car.make}
                  </span>
                  <span className="cdp-sidebar-title-row">
                    {car.model}
                    {car.trim ? ` ${car.trim}` : ''}
                  </span>
                </h1>
                <div className="cdp-info-badges">
                  {car.stockNumber && (
                    <span
                      className={`cdp-stock-badge ${stockCopied ? 'copied' : ''}`}
                      onClick={handleCopyStockNumber}
                      title={stockCopied ? "Copied!" : "Copy stock number"}
                    >
                      {stockCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      <span className="cdp-badge-label">Stock #</span>{car.stockNumber}
                    </span>
                  )}
                  {car.vin && (
                    <span
                      className={`cdp-stock-badge cdp-vin-badge ${vinCopied ? 'copied' : ''}`}
                      onClick={handleCopyVIN}
                      title={vinCopied ? "Copied!" : "Copy VIN"}
                    >
                      {vinCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      <span className="cdp-badge-label">VIN #</span> {car.vin}
                    </span>
                  )}
                </div>
              </div>

              <div className="cdp-price-section">
                <div className="cdp-price-row">
                  <div className="cdp-price-current">{formatPrice(car.price)}</div>
                  {car.mileage && (
                    <>
                      <span className="cdp-price-separator">|</span>
                      <div className="cdp-price-miles">{formatMiles(car.mileage)}</div>
                    </>
                  )}
                  {car.isPriceDrop && car.originalPrice && (
                    <>
                      <div className="cdp-price-drop-badge">
                        <ArrowDown size={14} />
                      </div>
                      <div className="cdp-price-original">
                        was {formatPrice(car.originalPrice)}
                      </div>
                    </>
                  )}
                </div>
                <div className="cdp-payment">
                  <span>Estimated ${monthlyPayment}/mo</span>
                  <small>$0 cash down</small>
                </div>
              </div>

              <div className="cdp-shipping">
                <Truck size={16} />
                {shippingInfo ? (
                  <div>
                    <span>{`Estimated $${shippingInfo.estimateMin || shippingInfo.shippingCost}-$${shippingInfo.estimateMax || shippingInfo.shippingCost} shipping`} · {shippingInfo.distance} mi</span>
                    <small>{getDeliveryDate(shippingInfo.estimatedDays)} · Final quote at checkout</small>
                  </div>
                ) : <span>Delivery starts at $299 · Final quote at checkout</span>}
              </div>

              <button
                className="cdp-cta"
                onClick={() => {
                  // Check if user is logged in
                  if (isAuthenticated && user) {
                    // User is logged in, go directly to appointment booking
                    navigate(`/appointment/${car.stockNumber}`);
                  } else {
                    // User is not logged in, redirect to login with return path
                    navigate(`/auth?redirect=/appointment/${car.stockNumber}`);
                  }
                }}
              >
                <Calendar size={18} />Schedule Test Drive
              </button>
              <button
                type="button"
                className="cdp-cta cdp-cta-secondary"
                onClick={() => {
                  const visitPath = `/profile?tab=interestedIn&carId=${car.id}`;
                  if (isAuthenticated && user) {
                    navigate(visitPath);
                  } else {
                    navigate(`/auth?redirect=${encodeURIComponent(visitPath)}`);
                  }
                }}
              >
                <MapPin size={18} /> Interested in this car
              </button>
            </div>
          </aside>
        </div>
      </div>
      <Footer />

      {/* Social Share Modal */}
      {showSocialShare && (
        <SocialShare car={car} onClose={() => setShowSocialShare(false)} />
      )}

      {/* Email Car Details Modal */}
      {showEmailCar && (
        <EmailCarDetails car={car} onClose={() => setShowEmailCar(false)} />
      )}

      {/* Virtual Tour Viewer */}
      {showVirtualTour && hasVirtualTour && (
        <VirtualTourViewer
          images={virtualTourImages}
          views={virtualTourViews} // Support multiple views: { exterior: [...], interior: [...] }
          onClose={() => setShowVirtualTour(false)}
          carName={`${car.year} ${car.make} ${car.model}`}
        />
      )}

      {/* Inspection Popup - 150-point list */}
      {showInspectionPopup && (
        <div className="cdp-inspection-popup-overlay" onClick={() => setShowInspectionPopup(false)}>
          <div className="cdp-inspection-popup" onClick={(e) => e.stopPropagation()}>
            <div className="cdp-inspection-popup-header">
              <h2>150+ Point Vehicle Inspection</h2>
              <button className="cdp-inspection-popup-close" onClick={() => setShowInspectionPopup(false)} aria-label="Close">
                <X size={24} />
              </button>
            </div>
            <p className="cdp-inspection-popup-subtitle">
              Our technicians spend hours inspecting and reconditioning each car to meet our standards. 
              This list may vary by vehicle type, such as electric, hybrid, and plug-in hybrid vehicles.
            </p>
            <div className="cdp-inspection-popup-content">
              {INSPECTION_CATEGORIES.map((category, catIndex) => (
                <div key={catIndex} className="cdp-inspection-popup-category">
                  <h3>{category.name}</h3>
                  <ul>
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <Check size={16} className="cdp-inspection-check" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CarDetails;